import { lazy, useEffect, useState } from 'react';
import { trackEvent } from 'utils/tracking';
import { subscribeToApsisNewsletter } from '../../../utils/apsis';
import { locationIsKia, validEmail } from '../../../utils/helpers';
import { AppProps } from '../../App';
import Snippet from '../../components/Snippet';
import PrimaryButton from '../../components/ui/PrimaryButton';

const NewsLetterControls = lazy(
  () => import('../../components/newsletter/NewsletterControls'),
);

export enum ThemeEnum {
  GREY = 'Grey',
  LIGHT = 'Light',
  DARK = 'Dark',
}

export type NewsletterOptions = Pick<
  AppProps,
  | 'theme'
  | 'heading'
  | 'description'
  | 'buttonLabel'
  | 'checkboxLabel'
  | 'emailPlaceholder'
  | 'thankYouMessage'
>;

export const defaultOptions = {
  theme: ThemeEnum.GREY,
  heading: 'Nyhetsbrev',
  description:
    'Få de senaste nyheterna kring lanseringar av nya bilar, kampanjer, event och andra spännande reportage genom att gå med i nyhetsbrevet.',
  buttonLabel: 'Gå med i nyhetsbrev',
  checkboxLabel:
    'Nyhetsbrev. Jag samtycker till att Kia Sverige AB behandlar mina personuppgifter och förser mig med information från Kia Sverige. Jag kan när som helst säga upp prenumerationen på ett visst e-postnyhetsbrev eller liknande kommunikation genom att följa anvisningarna i respektive kommunikation.',
  emailPlaceholder: 'Ange din e-postadress',
  thankYouMessage: 'Tack för att du prenumererar på vårt nyhetsbrev!',
};

const NewsLetter = ({
  hideControls,
  onSettingsChange,
  ...optionProps
}: Partial<NewsletterOptions> & {
  hideControls?: boolean;
  onSettingsChange?: (n: NewsletterOptions) => void;
}) => {
  const [email, setEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [options, setOptions] =
    useState<Required<NewsletterOptions>>(defaultOptions);
  const [error, setError] = useState<null | string>(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>(
    'idle',
  );

  useEffect(() => {
    if (onSettingsChange) {
      onSettingsChange(options);
    }
  }, [options]);

  useEffect(() => {
    setOptions((prevOptions) => {
      if (
        Object.keys(optionProps).every(
          (key) =>
            prevOptions[key as keyof NewsletterOptions] ===
            optionProps[key as keyof NewsletterOptions],
        )
      ) {
        return prevOptions;
      }

      return {
        ...prevOptions,
        ...optionProps,
      };
    });
  }, [optionProps]);

  const handleFormError = () => {
    setError(null);
    if (!email) {
      setError('Vänligen ange din e-postadress');
      return false;
    } else if (!acceptedTerms) {
      setError('Vänligen acceptera villkoren');
      return false;
    } else if (!validEmail(email)) {
      setError('Vänligen ange en giltig e-postadress');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setFormState('loading');
    const valid = handleFormError();
    if (!valid) {
      setFormState('idle');
      return;
    }

    const { status } = await subscribeToApsisNewsletter({
      email,
      url: window.location.href,
    });
    if (status !== 200) {
      setError('Något gick fel, vänligen försök igen senare');
      setFormState('idle');
      return;
    }
    trackEvent({ event: 'newsletter_signup' });
    
    setFormState('success');
  };

  const themeClasses = () => {
    switch (options.theme) {
      case ThemeEnum.LIGHT:
        return 'bg-white border border-black border-opacity-10';
      case ThemeEnum.DARK:
        return 'bg-black !text-white';
      default:
        return 'bg-kia-grey';
    }
  };

  const getSnippetProps = () => {
    return Object.entries(options)
      .map(([key, value]) => {
        return value ? `${key}="${value.replaceAll('"', "'")}"` : '';
      })
      .join(' ');
  };

  return (
    <div className="max-w-screen-kia-sm mx-auto relative px-4">
      {!locationIsKia() && !hideControls ? (
        <>
          <NewsLetterControls setOptions={setOptions} />
          <Snippet page="newsletter" props={getSnippetProps()} />
        </>
      ) : null}

      <div
        className={`p-7 text-center grid sm:grid-cols-2 gap-6 my-4 ${themeClasses()}`}
      >
        {formState === 'success' ? (
          <div className="col-span-2 px-10 py-20">
            <h2 className="large">{options.thankYouMessage}</h2>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-5">
              <h2 className="large sm:text-left">{options.heading}</h2>
              <p className="text-left">{options.description}</p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="grid gap-4"
            >
              <div className="w-full flex flex-col gap-4">
                <label htmlFor="newsletterEmail" className="sr-only">
                  {options.emailPlaceholder}
                </label>
                <input
                  type="email"
                  name="newsletterEmail"
                  autoComplete="email"
                  id="newsletterEmail"
                  placeholder={options.emailPlaceholder}
                  className="border-black border p-4 w-full h-auto placeholder:text-black !text-black focus-visible:outline-black"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <label htmlFor="acceptTerms" className="flex gap-2 w-full">
                  <span>
                    <input
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                      }}
                      id="acceptTerms"
                      checked={acceptedTerms}
                      type="checkbox"
                      className="self-start"
                      title={options.checkboxLabel}
                    />
                  </span>
                  <div className="relative text-left">
                    <p
                      className={`underline text-left text-sm pr-4 ${termsOpen ? '' : 'line-clamp-1'}`}
                      dangerouslySetInnerHTML={{
                        __html: options.checkboxLabel,
                      }}
                    ></p>
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        setTermsOpen(!termsOpen);
                      }}
                      className={`absolute right-0 top-0 ${termsOpen ? 'rotate-180' : ''}`}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.5 6.75L9 11.25L13.5 6.75"
                          stroke={
                            ThemeEnum.DARK === options.theme ? 'white' : 'black'
                          }
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </label>
              </div>
              <div className="flex flex-col gap-2">
                <PrimaryButton
                  state={formState === 'loading' ? 'loading' : 'idle'}
                  mode="dark"
                  type="submit"
                >
                  {options.buttonLabel}
                </PrimaryButton>
                {error ? <span className="text-red-500">{error}</span> : null}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsLetter;
